import {toIntent, type Intent, assignProps} from '@sail/engine';
import {useRef, useState, useCallback} from 'react';
import type {UIEvent, RefObject} from 'react';
import {useResizeObserver} from '@sail/react-aria';
import {tokens} from '../tokens';
import {DialogContentConfig} from '../components/dialog/DialogContent';
import {DialogFooterConfig} from '../components/dialog/DialogFooter';
import {DialogHeaderConfig} from '../components/dialog/DialogHeader';
import {css} from '../css';

export const useOverflowBorders = (
  /* the ref for the container element */
  ref: RefObject<HTMLDivElement>,
  noHeader: boolean,
  noFooter: boolean,
): Intent => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const onResize = useCallback(() => {
    if (contentRef && contentRef.current) {
      setIsOverflowing(
        contentRef.current.scrollHeight > contentRef.current.clientHeight,
      );
    }
  }, []);

  useResizeObserver({ref, onResize});

  const handleOnScroll = (event: UIEvent<HTMLDivElement>) => {
    const el = event.target as HTMLElement;
    setIsScrolled(el.scrollTop > 0);
    if (contentRef && contentRef.current) {
      // if the user has scrolled to the bottom, set isOverflowing to false
      const scrolledToBottom =
        contentRef.current.scrollTop ===
        contentRef.current.scrollHeight - contentRef.current.offsetHeight;
      setIsOverflowing(!scrolledToBottom);
    }
  };
  return toIntent([
    DialogContentConfig.customize({
      css: {
        paddingTop: noHeader ? undefined : '1px',
        paddingBottom: noFooter ? undefined : '1px',
      },
      uses: [assignProps({ref: contentRef, onScroll: handleOnScroll})],
    }),
    DialogFooterConfig.customize({
      uses: [
        css({
          borderBlockStart: isOverflowing
            ? `1px solid ${tokens.color.border}`
            : 'none',
        }),
      ],
    }),
    DialogHeaderConfig.customize({
      uses: [
        css({
          borderBlockEnd: isScrolled
            ? `1px solid ${tokens.color.border}`
            : 'none',
        }),
      ],
    }),
  ]);
};
